import { useRef, useCallback, type KeyboardEvent } from 'react';
import { SparkIcon } from '../SparkIcon';
import { TooltipTrigger } from '../Tooltip/TooltipTrigger';
import { useControllableState } from '../../hooks/useControllableState';
import './SegmentedControlIcon.css';

export type SegmentedControlIconSize = 'small' | 'medium';
export type SegmentedControlIconItemPosition = 'first' | 'last';
export type SegmentedControlIconItemVisualState = 'default' | 'hover' | 'pressed' | 'disabled';

export type SegmentedControlIconItemData = {
  id: string;
  /** Accessible name / tooltip text (required per Figma docs). */
  label: string;
  iconSlug: string;
  /** Override default icon size for this segment. */
  iconSize?: 'small' | 'medium';
  disabled?: boolean;
};

/** Single icon segment button inside a SegmentedControlIcon. */
export type SegmentedControlIconItemProps = {
  label: string;
  active?: boolean;
  size?: SegmentedControlIconSize;
  position?: SegmentedControlIconItemPosition;
  disabled?: boolean;
  iconSlug?: string;
  iconSize?: 'small' | 'medium';
  visualState?: SegmentedControlIconItemVisualState;
  onClick?: () => void;
  className?: string;
};

/** Two-segment icon toggle (e.g. grid/list view switch). Controlled and uncontrolled. */
export type SegmentedControlIconProps = {
  /** Exactly two items. */
  items: [SegmentedControlIconItemData, SegmentedControlIconItemData];
  /** Controlled active item id. */
  activeId?: string;
  /** Initial active item id for uncontrolled mode. */
  defaultActiveId?: string;
  onChange?: (id: string) => void;
  size?: SegmentedControlIconSize;
  className?: string;
};

function defaultIconSize(controlSize: SegmentedControlIconSize): 'small' | 'medium' {
  return controlSize === 'small' ? 'small' : 'medium';
}

export function SegmentedControlIconItem({
  label,
  active = false,
  size = 'medium',
  position = 'first',
  disabled = false,
  iconSlug = 'table',
  iconSize,
  visualState,
  onClick,
  className = '',
}: SegmentedControlIconItemProps) {
  const isDisabled = disabled || visualState === 'disabled';
  const resolvedIconSize = iconSize ?? defaultIconSize(size);

  const itemClasses = [
    'spark-segmented-control-icon-item',
    `spark-segmented-control-icon-item-${position}`,
    active ? 'spark-segmented-control-icon-item-active' : '',
    isDisabled ? 'spark-segmented-control-icon-item-disabled' : '',
    visualState === 'hover' && !active && !isDisabled ? 'spark-segmented-control-icon-item-state-hover' : '',
    visualState === 'pressed' && !active && !isDisabled ? 'spark-segmented-control-icon-item-state-pressed' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <TooltipTrigger text={label} disabled={isDisabled}>
      <button
        type="button"
        role="tab"
        className={itemClasses}
        disabled={isDisabled}
        aria-selected={active}
        aria-label={label}
        onClick={onClick}
        data-size={size}
      >
        <SparkIcon slug={iconSlug} size={resolvedIconSize} className="spark-segmented-control-icon-item-icon" />
      </button>
    </TooltipTrigger>
  );
}

export function SegmentedControlIcon({
  items,
  activeId: activeIdProp,
  defaultActiveId,
  onChange,
  size = 'medium',
  className = '',
}: SegmentedControlIconProps) {
  const [activeId, setActiveId] = useControllableState(
    activeIdProp,
    defaultActiveId ?? items[0]?.id ?? '',
    onChange,
  );

  const containerRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();

      const enabledItems = items.filter((item) => !item.disabled);
      if (enabledItems.length === 0) return;

      const currentIndex = enabledItems.findIndex((item) => item.id === activeId);
      let nextIndex: number;

      if (e.key === 'ArrowRight') {
        nextIndex = currentIndex >= enabledItems.length - 1 ? 0 : currentIndex + 1;
      } else {
        nextIndex = currentIndex <= 0 ? enabledItems.length - 1 : currentIndex - 1;
      }

      const nextId = enabledItems[nextIndex].id;
      setActiveId(nextId);

      const buttons = containerRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
      const fullIndex = items.findIndex((item) => item.id === nextId);
      buttons?.[fullIndex]?.focus();
    },
    [items, activeId, setActiveId],
  );

  const rootClasses = [
    'spark-segmented-control-icon',
    `spark-segmented-control-icon-${size}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const positions: SegmentedControlIconItemPosition[] = ['first', 'last'];

  return (
    <div className={rootClasses} role="tablist" aria-label="View mode" ref={containerRef} onKeyDown={handleKeyDown}>
      {items.map((item, index) => (
        <SegmentedControlIconItem
          key={item.id}
          label={item.label}
          active={item.id === activeId}
          size={size}
          position={positions[index]}
          disabled={item.disabled}
          iconSlug={item.iconSlug}
          iconSize={item.iconSize}
          onClick={() => setActiveId(item.id)}
        />
      ))}
    </div>
  );
}

/** Default table master-row view toggle (Figma: table + layout-rows). */
export const TABLE_VIEW_TOGGLE_ITEMS: [SegmentedControlIconItemData, SegmentedControlIconItemData] = [
  { id: 'grid', label: 'Grid view', iconSlug: 'table' },
  { id: 'list', label: 'List view', iconSlug: 'layout-rows' },
];
